import type { ReactNode } from 'react';
import type { CouponFactsDto } from '../types.ts';

export interface FactsDraft {
  kind: 'code' | 'deal';
  title: string;
  code: string;
  description: string;
  discountType: CouponFactsDto['discountType'];
  discountValue: string;
  minPurchase: string;
  conditions: string;
  newCustomersOnly: boolean;
  exclusive: boolean;
  flags: string[];
  startsAt: string;
  endsAt: string;
}

/** Coupon flags (docs/coupon-flags.md): filter pills on store pages; audience flags must be named in the copy. */
export const COUPON_FLAG_OPTIONS: { value: string; label: string; audience?: boolean }[] = [
  { value: 'first_order', label: 'First order' },
  { value: 'free_delivery', label: 'Free delivery' },
  { value: 'military', label: 'Military', audience: true },
  { value: 'student', label: 'Students', audience: true },
  { value: 'key_workers', label: 'Key workers / first responders', audience: true },
  { value: 'seniors', label: 'Seniors', audience: true },
  { value: 'teachers', label: 'Teachers', audience: true },
  { value: 'members', label: 'Members / loyalty', audience: true },
  { value: 'app', label: 'App only' },
  { value: 'in_store', label: 'In store' },
  { value: 'newsletter', label: 'Newsletter sign-up' },
];

/** `datetime-local` wants local time without a zone. */
const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};
const fromLocalInput = (value: string) => (value ? new Date(value).toISOString() : null);
const numberOrNull = (value: string) => (value.trim() === '' ? null : Number(value));

export const emptyFacts = (): FactsDraft => ({
  kind: 'code',
  title: '',
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: '',
  minPurchase: '',
  conditions: '',
  newCustomersOnly: false,
  exclusive: false,
  flags: [],
  startsAt: '',
  endsAt: '',
});

export const factsFromDto = (c: CouponFactsDto): FactsDraft => ({
  kind: c.kind,
  title: c.title,
  code: c.code ?? '',
  description: c.description ?? '',
  discountType: c.discountType,
  discountValue: c.discountValue === null ? '' : String(c.discountValue),
  minPurchase: c.minPurchase === null ? '' : String(c.minPurchase),
  conditions: c.conditions ?? '',
  newCustomersOnly: c.newCustomersOnly,
  exclusive: c.exclusive,
  flags: c.flags ?? [],
  startsAt: toLocalInput(c.startsAt),
  endsAt: toLocalInput(c.endsAt),
});

export const factsToInput = (d: FactsDraft) => ({
  kind: d.kind,
  title: d.title,
  code: d.kind === 'code' ? d.code : null,
  description: d.description,
  discountType: d.discountType,
  discountValue: d.discountType === 'percent' || d.discountType === 'amount' ? numberOrNull(d.discountValue) : null,
  minPurchase: numberOrNull(d.minPurchase),
  conditions: d.conditions,
  newCustomersOnly: d.newCustomersOnly,
  exclusive: d.exclusive,
  flags: d.flags,
  startsAt: fromLocalInput(d.startsAt),
  endsAt: fromLocalInput(d.endsAt),
});

/** The country-level fields of a coupon, used when creating and editing. */
/** Extra UI next to (action) and under (below) a field, e.g. the magic wand on the conditions. */
export type FieldExtras = Partial<Record<'conditions', { action?: ReactNode; below?: ReactNode }>>;

export function CouponFactsFields({ draft, onChange, disabled = false, extras = {} }: { draft: FactsDraft; onChange: (draft: FactsDraft) => void; disabled?: boolean; extras?: FieldExtras }) {
  const set = <K extends keyof FactsDraft>(key: K, value: FactsDraft[K]) => onChange({ ...draft, [key]: value });
  return (
    <div className="form-grid">
      <fieldset className="span-2 radio-row" disabled={disabled}>
        <legend>Kind</legend>
        <label className="inline-check">
          <input type="radio" name="kind" checked={draft.kind === 'code'} onChange={() => set('kind', 'code')} /> Code (shopper types it at checkout)
        </label>
        <label className="inline-check">
          <input type="radio" name="kind" checked={draft.kind === 'deal'} onChange={() => set('kind', 'deal')} /> Deal (no code needed)
        </label>
      </fieldset>
      <label className="span-2">
        Title
        <input required minLength={3} value={draft.title} disabled={disabled} onChange={(e) => set('title', e.target.value)} />
      </label>
      {draft.kind === 'code' ? (
        <label>
          Code
          <input required value={draft.code} disabled={disabled} onChange={(e) => set('code', e.target.value.toUpperCase())} className="mono" />
        </label>
      ) : null}
      <label>
        Discount
        <span className="input-pair">
          <select value={draft.discountType} disabled={disabled} onChange={(e) => set('discountType', e.target.value as FactsDraft['discountType'])}>
            <option value="percent">% off</option>
            <option value="amount">Amount off</option>
            <option value="free_shipping">Free delivery</option>
            <option value="other">Other</option>
          </select>
          {draft.discountType === 'percent' || draft.discountType === 'amount' ? (
            <input type="number" min={0} step="0.01" aria-label="Discount value" value={draft.discountValue} disabled={disabled} onChange={(e) => set('discountValue', e.target.value)} />
          ) : null}
        </span>
      </label>
      <label>
        Minimum spend
        <input type="number" min={0} step="0.01" value={draft.minPurchase} disabled={disabled} onChange={(e) => set('minPurchase', e.target.value)} />
      </label>
      <label>
        Starts
        <input type="datetime-local" value={draft.startsAt} disabled={disabled} onChange={(e) => set('startsAt', e.target.value)} />
      </label>
      <label>
        Ends
        <input type="datetime-local" value={draft.endsAt} disabled={disabled} onChange={(e) => set('endsAt', e.target.value)} />
      </label>
      <div className="span-2 field-block">
        <div className="field-head">
          <label htmlFor="facts-conditions">
            Conditions <small className="muted">one per line</small>
          </label>
          {extras.conditions?.action}
        </div>
        <textarea id="facts-conditions" rows={3} value={draft.conditions} disabled={disabled} onChange={(e) => set('conditions', e.target.value)} />
        {extras.conditions?.below}
      </div>
      <label className="span-2">
        Description
        <textarea rows={2} value={draft.description} disabled={disabled} onChange={(e) => set('description', e.target.value)} />
      </label>
      <div className="span-2 radio-row">
        <label className="inline-check">
          <input type="checkbox" checked={draft.newCustomersOnly} disabled={disabled} onChange={(e) => set('newCustomersOnly', e.target.checked)} /> New customers only
        </label>
        <label className="inline-check">
          <input type="checkbox" checked={draft.exclusive} disabled={disabled} onChange={(e) => set('exclusive', e.target.checked)} /> Exclusive
        </label>
      </div>
      <fieldset className="span-2 flag-row" disabled={disabled}>
        <legend>Flags</legend>
        <span className="muted small">Filter pills on the store page. Who it is for (military, students …) must be in every site's title and description; new copy names it.</span>
        <div className="radio-row">
          {COUPON_FLAG_OPTIONS.map((flag) => (
            <label key={flag.value} className="inline-check">
              <input
                type="checkbox"
                checked={draft.flags.includes(flag.value)}
                onChange={(e) => set('flags', e.target.checked ? [...draft.flags, flag.value] : draft.flags.filter((f) => f !== flag.value))}
              />{' '}
              {flag.label}
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
